/**
 * Main App Component
 * ==================
 * Root component with React Router configuration.
 * All routes are defined here for the Gup Shup website.
 */

import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect, Suspense, Component } from 'react';
import { AnimatePresence, motion, MotionConfig } from 'framer-motion';

// Layout Components
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import Button from './components/ui/Button';

// Home stays a regular import (most common entry point, shouldn't pay a
// dynamic-import round trip); every other page is code-split - see
// src/pages/routes.js for the loaders and why.
import Home from './pages/Home';
import {
  About, Menu, Ordering, Reservations, Rewards, Location, Contact, PrivacyPolicy, Terms,
  PRIORITY_PREFETCH_PATHS, prefetchRoute
} from './pages/routes';

// Styles
import './App.css';

/**
 * ScrollToTop Component
 * Scrolls to top of page on route change
 */
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

/**
 * Page Layout Wrapper
 */
const PageLayout = ({ children }) => {
  return (
    <div className="flex flex-col min-h-screen bg-ink">
      <Navigation />
      <main className="flex-grow pt-20 lg:pt-24">
        {children}
      </main>
      <Footer />
    </div>
  );
};

/**
 * 404 Not Found Page
 */
const NotFound = () => {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="text-center px-4">
        <h1 className="text-7xl text-amber-bright mb-4">404</h1>
        <h2 className="text-2xl text-cream mb-4">Page Not Found</h2>
        <p className="text-cream-dim mb-8 font-sans">
          Sorry, we couldn't find the page you're looking for.
        </p>
        <Button to="/">Go Back Home</Button>
      </div>
    </div>
  );
};

/**
 * RouteErrorBoundary
 * Catches a code-split chunk failing to load (e.g. a flaky connection)
 * instead of a hard crash / blank screen. Keyed by pathname in
 * AnimatedRoutes below, so navigating away from a broken page mounts a
 * fresh boundary rather than staying stuck in the error state.
 */
class RouteErrorBoundary extends Component {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    // eslint-disable-next-line no-console
    console.error('Route failed to load:', error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center px-4">
            <h2 className="text-2xl text-cream mb-4 font-display">Something went wrong</h2>
            <p className="text-cream-dim mb-8 font-sans">
              This page didn't load correctly. Please refresh and try again.
            </p>
            <Button onClick={() => window.location.reload()}>Refresh</Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * Warms the code-split chunks for the pages visitors go looking for most
 * (Menu, Order Online, Book a Table, Rewards, Location) once the browser
 * is idle, so navigation feels instant even on touch devices where hover
 * never fires to trigger the on-demand prefetch in Button/Navigation.
 * Runs once; a slow connection or a mid-priority tab just means these
 * fetches queue behind whatever the browser is already doing.
 */
const usePrefetchPriorityRoutes = () => {
  useEffect(() => {
    const warm = () => PRIORITY_PREFETCH_PATHS.forEach(prefetchRoute);
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(warm, { timeout: 2000 });
      return () => window.cancelIdleCallback(id);
    }
    const id = window.setTimeout(warm, 1500);
    return () => window.clearTimeout(id);
  }, []);
};

/**
 * AnimatedRoutes
 * Cross-fades the routed page content on navigation so page changes feel
 * seamless rather than an instant swap. Keyed on pathname only (not the
 * full location) so in-page hash navigation — e.g. Ordering's #delivery
 * anchor — never retriggers the transition. Opacity-only, no vertical
 * movement, so it never competes with each page's own Reveal entrances.
 */
const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
      >
        <RouteErrorBoundary>
          <Suspense fallback={null}>
            <Routes location={location}>
              {/* Main Pages */}
              <Route path="/" element={<Home />} />
              <Route path="/about-us" element={<About />} />
              <Route path="/menu" element={<Menu />} />
              <Route path="/order-online" element={<Ordering />} />
              <Route path="/book-table" element={<Reservations />} />
              <Route path="/rewards" element={<Rewards />} />
              <Route path="/location" element={<Location />} />
              <Route path="/contact-us" element={<Contact />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<Terms />} />

              {/* Legacy/alternate paths - real HTTP 301s now happen at the
                  edge (vercel.json) before these ever get hit; kept here
                  only as a harmless client-side fallback. */}
              <Route path="/about" element={<Navigate to="/about-us" replace />} />
              <Route path="/contact" element={<Navigate to="/contact-us" replace />} />
              <Route path="/reservations" element={<Navigate to="/book-table" replace />} />
              <Route path="/book" element={<Navigate to="/book-table" replace />} />
              <Route path="/order" element={<Navigate to="/order-online" replace />} />
              <Route path="/find-us" element={<Navigate to="/location" replace />} />

              {/* 404 Catch-all */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </RouteErrorBoundary>
      </motion.div>
    </AnimatePresence>
  );
};

/**
 * Main App Component
 */
function App() {
  usePrefetchPriorityRoutes();

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ScrollToTop />
      {/* reducedMotion="user" makes every Framer Motion animation in the
          tree — this one included — auto-disable transform/layout motion
          when the OS prefers-reduced-motion is set, without threading a
          manual check through each new component. */}
      <MotionConfig reducedMotion="user">
        <PageLayout>
          <AnimatedRoutes />
        </PageLayout>
      </MotionConfig>
    </Router>
  );
}

export default App;
